import { useState, useMemo } from "react"
import { isEmpty, map, size, chain, toLower, remove, get, filter } from "lodash"
import { TextField, Tabs, Tab, List, ListItemText, ListItemAvatar, Divider, ListItemSecondaryAction, ListItemButton, InputAdornment } from "@mui/material"
import { Search, SelectAll, Straighten } from "@mui/icons-material"
import PropTypes from "prop-types"
import { ITEM_SHAPE } from "./shapes"
import { camelCaseToSpace } from "./helper"
import { createMatch } from "helpers/string"
import "./styles.scss"

const DEFAULT_TAB_ORDER = {
  all: 0,
  style: 1,
  country: 2,
  currency: 3,
  sector: 4,
}

function getResultKey({ type, id, classificationId }) {
  return `${type}-${classificationId || type}-${id}`
}

function getItemType({ type, classificationTier, contentSetId, category }) {
  return classificationTier || contentSetId || category || type
}

const getItemIcon = (item) => {
  switch (item.type) {
    case "factor":
      return <SelectAll />
    default:
      return <Straighten />
  }
}

export default function SearchFactor({ selectItem, applyAction, factors, factorValueLabel, multiSelect, tabOrderOverride, variant = "standard" }) {
  const [searchValue, setSearch] = useState("")
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [selectedTab, setSelectedTab] = useState("all")

  const tabOrder = useMemo(() => {
    return tabOrderOverride || DEFAULT_TAB_ORDER
  }, [tabOrderOverride])

  const searchResults = useMemo(() => {
    const match = createMatch(searchValue)
    const results = filter(factors, factor => match(get(factor, "name")))

    const tabs = [
      { id: "all", count: results.length, disabled: results.length === 0 },
      ...chain(results)
        .groupBy(getItemType)
        .map((value, key) => ({
          id: key,
          count: value.length,
          disabled: value.length === 0,
          sort: get(tabOrder, toLower(key)),
        }))
        .sortBy("sort")
        .value(),
    ]

    const notInSelectedTab = element => selectedTab !== "all" && getItemType(element) !== selectedTab
    remove(results, notInSelectedTab)

    return {
      tabs,
      items: results,
    }
  }, [factors, searchValue, selectedTab, tabOrder])

  const noItems = useMemo(() => {
    return size(searchResults.items) === 0
  }, [searchResults])

  const onChange = (event) => {
    const { value } = event.target
    setSearch(value)
  }

  const handleSelectItem = (item) => {
    if (multiSelect) {
      selectItem(item)
    } else if (applyAction) {
      applyAction(() => selectItem(item))()
    } else {
      selectItem(item)
    }
  }

  const onKeyPress = (event) => {
    switch (event.key) {
      case "ArrowDown":
        setSelectedIndex(selectedIndex + 1)
        break
      case "ArrowUp":
        setSelectedIndex(selectedIndex - 1)
        break
      case "Enter": {
        const { items } = searchResults
        if (selectedIndex >= 0 && items.length > selectedIndex) {
          handleSelectItem(items[selectedIndex])
        }
        break
      }
    }
  }

  const { tabs, items } = searchResults
  return (<div className="components_search-factors modules_unified-search">
    <TextField
      variant={variant}
      tabIndex={0}
      onKeyDown={onKeyPress}
      placeholder="Search…"
      value={searchValue}
      autoFocus
      onChange={onChange}
      slotProps={{
        root: {
          className: "search-input-wrapper",
        },
        htmlInput: {
          className: "search-input",
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        },
      }}
    />
    {(!isEmpty(tabs) && !noItems) && <Tabs
      scrollButtons="auto"
      variant="scrollable"
      className="tabs"
      value={selectedTab}
      indicatorColor="primary"
      textColor="primary"
      onChange={(_, value) => setSelectedTab(value)}
    >
      {map(tabs, ({ id, count, disabled }) => (<Tab key={id} value={id} label={`${camelCaseToSpace(id)} (${count})`} disabled={disabled} />))}
    </Tabs>}
    {!noItems
      && <div className="search-box">
        {factorValueLabel && <div className="sub-header"><span>{factorValueLabel}</span></div>}
        <List component="nav">
          {map(items, (item, index) =>
            <ListItemButton
              tabIndex={-1}
              key={getResultKey(item)}
              disabled={item.disabled}
              onClick={() => { handleSelectItem(item) }}
              selected={index === selectedIndex}
            >
              <ListItemAvatar>
                {getItemIcon(item)}
              </ListItemAvatar>
              <ListItemText primary={item.name || item.alias || item.label} secondary={item.info} />
              {/* [TODO] This is not actually an action. No point using a button. */}
              {factorValueLabel && item.value && <ListItemSecondaryAction>
                {item.value}
              </ListItemSecondaryAction>}
            </ListItemButton>,
          )}
        </List>
        <Divider />
      </div>}
    {searchValue && noItems
      && <div className="search-box">
        <div className="search-empty">No factors matching your search</div>
      </div>}
  </div>)
}

SearchFactor.propTypes = {
  selectItem: PropTypes.func.isRequired,
  applyAction: PropTypes.func,
  factorValueLabel: PropTypes.string,
  multiSelect: PropTypes.bool,
  tabOrderOverride: PropTypes.object,
  variant: PropTypes.oneOf(["standard", "outlined", "filled"]),
  factors: PropTypes.arrayOf(ITEM_SHAPE).isRequired,
}
